/**
 * NativeEspnHandoff — what AddLeagueModal shows for ESPN on native instead
 * of the extension/manual-paste flow.
 *
 * Neither of AddLeagueModal's two ESPN paths work on an iPhone: there are no
 * browser extensions on iOS, and there's no DevTools to copy espn_s2 out of
 * manually. Rather than offer a flow that can only fail, this is a
 * deliberate, designed dead end with one real action: email the signed-in
 * user a link to finish on a desktop. See routes/espnConnect.js
 * (POST /handoff-email) and emailService.sendEspnDesktopHandoff.
 *
 * This is the screen the App Store plan's Part 3A calls "the screen most
 * likely to decide the review" — it has to read as intentional, not broken.
 */

import { useState } from 'react'
import { authApi } from '../services/api'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'

export default function NativeEspnHandoff() {
  const [status, setStatus] = useState('idle') // 'idle' | 'sending' | 'sent' | 'error'

  const handleSend = async () => {
    setStatus('sending')
    try {
      await authApi.sendEspnHandoffEmail()
      setStatus('sent')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="rounded-xl p-5 space-y-4" style={{ background: JAI_COLORS.navy }}>
      <div className="flex items-start gap-3">
        {/* A laptop glyph — reads as "this needs a computer" at a glance,
            before the text is even read. */}
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
          <rect x="3" y="4" width="18" height="12" rx="1.5" stroke="white" strokeWidth="1.5" />
          <path d="M1 19h22l-2-3H3l-2 3Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
        <div>
          <p className="text-sm font-semibold" style={{ color: 'white', fontFamily: JAI_FONTS.tight }}>
            ESPN leagues connect from a computer
          </p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.72)', lineHeight: 1.55 }}>
            ESPN's sign-in only works with a desktop browser, so connect once
            at play-jai.com on your laptop and your league appears here
            automatically.
          </p>
        </div>
      </div>

      {status === 'sent' ? (
        <div className="px-4 py-1 text-sm font-medium" style={{ borderLeft: '3px solid #1A8754', color: '#7FE0AB' }}>
          Sent — check your email for the link.
        </div>
      ) : (
        <button
          type="button"
          onClick={handleSend}
          disabled={status === 'sending'}
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-lg px-4 py-2.5 disabled:opacity-60"
          style={{ background: `linear-gradient(135deg, ${JAI_COLORS.blue} 0%, ${JAI_COLORS.blueBright} 100%)`, boxShadow: `0 4px 14px ${JAI_COLORS.blue}40` }}
        >
          {status === 'sending' ? 'Sending…' : 'Email me the link'}
        </button>
      )}

      {status === 'error' && (
        <p className="text-xs" style={{ color: '#FF8A8A' }}>
          Couldn't send that — please try again.
        </p>
      )}

      <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
        Yahoo and Sleeper leagues connect directly from your phone — no
        computer needed.
      </p>
    </div>
  )
}
